import * as XLSX from "xlsx";
import { COLUMN_MAX_EXPORT_WIDTH } from "@/lib/constants";
import type { CaseRecord } from "@/types";

/**
 * Safely get a field value from a CaseRecord, handling legacy spaced-key fallbacks.
 */
function getFieldValue(c: CaseRecord, field: string, ...fallbackKeys: string[]): string {
  const record = c as unknown as Record<string, unknown>;
  const val = record[field];
  if (val) return String(val);
  for (const key of fallbackKeys) {
    const fallback = record[key];
    if (fallback) return String(fallback);
  }
  return "";
}

/** Lower precedence index = wins when picking among structured outputs. */
const EXPORT_PA_STATUS_PRIORITY = [
  "approved",
  "denied",
  "pending",
  "cancelled",
  "historical_records_found",   // PA-history (MS drugs): treat like a completed outcome
  "not_required",
  "not_submitted_live",
  "not_submitted_ivr",
  "no_history_on_file_live",    // PA-history (MS drugs): semantically = not_submitted
  "no_history_on_file_ivr",     // PA-history (MS drugs): semantically = not_submitted
  "not_submitted",
  "other",
  "failed",
] as const;

function normalizePaToken(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, "_");
}

function effectiveStatusFromOutputs(outputs: Record<string, unknown>): string {
  const pa = String(outputs.pa_status ?? "").trim();
  if (pa) return normalizePaToken(pa);
  const fr = String(outputs.fail_reason ?? "").trim();
  if (fr) return "failed";
  return "";
}

function rankOfEffectiveStatus(eff: string): number {
  if (!eff) return 999;
  const t = eff;
  const idx = (EXPORT_PA_STATUS_PRIORITY as readonly string[]).indexOf(t);
  if (idx >= 0) return idx;
  return (EXPORT_PA_STATUS_PRIORITY as readonly string[]).indexOf("failed");
}

function consolidatePaStatusForExport(rawPa: string, hasFailReason: boolean): string {
  const t = normalizePaToken(rawPa);
  if (
    t === "not_submitted_live" ||
    t === "not_submitted_ivr" ||
    t === "no_history_on_file_live" ||
    t === "no_history_on_file_ivr"
  ) {
    return "not_submitted";
  }
  if (!rawPa.trim() && hasFailReason) return "failed";
  return rawPa.trim();
}

function pickOutputsForExport(c: CaseRecord): Record<string, unknown> {
  const hist = c.structured_outputs_history;
  if (hist && hist.length > 0) {
    let best: Record<string, unknown> = {};
    let bestRank = Infinity;
    let bestHistIdx = -1;

    for (let i = 0; i < hist.length; i++) {
      const outputs = (hist[i].outputs || {}) as Record<string, unknown>;
      const r = rankOfEffectiveStatus(effectiveStatusFromOutputs(outputs));
      if (r < bestRank || (r === bestRank && i > bestHistIdx)) {
        best = outputs;
        bestRank = r;
        bestHistIdx = i;
      }
    }
    return best;
  }
  if (c.structured_outputs) {
    return c.structured_outputs as Record<string, unknown>;
  }
  return {};
}

/**
 * Format patient/case data for export (CSV / Excel).
 * Column names align with intake CSV-style fields plus PA fields from prioritized structured output.
 */
function formatCaseDataForExport(data: CaseRecord[]) {
  const headers = [
    "case_id",
    "scheduled_call",
    "member_bin",
    "member_zip",
    "member_date_of_birth",
    "member_first_name",
    "member_id",
    "member_last_name",
    "member_pcn",
    "product_name_1",
    "provider_first_name",
    "provider_last_name",
    "provider_npi",
    "provider_zip",
    "pa_status",
    "details",
    "as_of_date",
  ];

  const rows = data.map((c) => {
    const structuredOutput = pickOutputsForExport(c);
    const rawPa = String(structuredOutput.pa_status ?? "").trim();
    const hasFailReason = Boolean(String(structuredOutput.fail_reason ?? "").trim());
    const paStatus = consolidatePaStatusForExport(rawPa, hasFailReason);
    const details =
      String(structuredOutput.pa_details ?? "").trim() ||
      String(structuredOutput.fail_details ?? "").trim() ||
      "";

    return [
      getFieldValue(c, "id", "case_id"),
      getFieldValue(c, "outbound_scheduled_for"),
      getFieldValue(c, "member_bin", "Member BIN"),
      getFieldValue(c, "member_zip", "Member Zip"),
      getFieldValue(c, "member_dob", "member_date_of_birth", "Member Date of Birth"),
      getFieldValue(c, "member_first_name", "Member First Name"),
      getFieldValue(c, "member_id", "Member ID"),
      getFieldValue(c, "member_last_name", "Member Last Name"),
      getFieldValue(c, "member_pcn", "Member PCN"),
      getFieldValue(
        c,
        "product_name",
        "product_name_1",
        "Product Name #1 (Human)",
        "Product Name #1",
      ),
      getFieldValue(c, "provider_first_name", "Provider First Name"),
      getFieldValue(c, "provider_last_name", "Provider Last Name"),
      getFieldValue(c, "provider_npi", "Provider NPI"),
      getFieldValue(c, "provider_zip", "Provider Zip"),
      paStatus,
      details,
      getFieldValue(c, "as_of_date", "As Of Date"),
    ];
  });

  return { headers, rows };
}

/**
 * Export cases as CSV file download.
 */
export function exportToCSV(data: CaseRecord[], filename: string) {
  const { headers, rows } = formatCaseDataForExport(data);

  const csvContent = [headers, ...rows]
    .map((row) =>
      row
        .map((cell) => {
          const cellStr = String(cell);
          if (
            cellStr.includes(",") ||
            cellStr.includes("\n") ||
            cellStr.includes('"')
          ) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        })
        .join(",")
    )
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export cases as Excel (.xlsx) file download.
 */
export function exportToXLS(data: CaseRecord[], filename: string) {
  const { headers, rows } = formatCaseDataForExport(data);

  const wb = XLSX.utils.book_new();
  const wsData = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Auto-size columns
  ws["!cols"] = headers.map((header, i) => {
    const maxLength = Math.max(
      header.length,
      ...rows.map((row) => String(row[i] || "").length)
    );
    return { wch: Math.min(maxLength + 2, COLUMN_MAX_EXPORT_WIDTH) };
  });

  XLSX.utils.book_append_sheet(wb, ws, "Case Data");
  XLSX.writeFile(wb, filename);
}
